import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

import { OfferBase } from 'models/offer.model';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-expiration-date',
  templateUrl: './expiration-date.component.html',
  styleUrls: ['./expiration-date.component.scss']
})
export class ExpirationDateComponent implements OnInit {
  form!: UntypedFormGroup;

  formReady = false;

  createdDate!: Date;

  initialExpirationDate!: Date;

  defaultDuration = 90;

  newExpirationDate!: Date;

  constructor(
    protected fb: UntypedFormBuilder,
    private storeService: StoreService,
    private formatDate: DatePipe
  ) { }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.setupForm();
  }

  private calculateDurationDays(): number {
    return Math.floor((this.newExpirationDate.getTime() - this.createdDate.getTime()) / 86400000);
  }

  private calculateExpirationDate(days: number): Date {
    return new Date(this.createdDate.getTime() + (days * 86400000));
  }

  private parseValidTo(validTo: string): Date {
    return new Date(`${(new Date(validTo)).toISOString().slice(0, 10)}${this.createdDate.toISOString().slice(10)}`);
  }

  setupForm(): void {
    this.storeService.setFormReady(this.formReady);

    this.storeService.getCurrentOffer()
      .pipe(take(1))
      .subscribe((data: OfferBase) => {
        const offer = data || {};
        this.createdDate = new Date(offer.createdDate || '');
        // eslint-disable-next-line no-multi-assign
        this.initialExpirationDate = this.newExpirationDate = offer.validTo
          ? this.parseValidTo(offer.validTo)
          : this.calculateExpirationDate(this.defaultDuration);

        this.form = this.fb.group({
          durationDays: [
            String(this.calculateDurationDays()),
            [Validators.required, Validators.min(0)]
          ],
          validTo: [this.formatDate.transform(this.newExpirationDate)]
        }, { updateOn: 'blur' });

        this.formReady = true;
        this.storeService.setFormReady(this.formReady);
        this.detectFormChanges();
      });
  }

  private hasDateChanged(a: Date, b: Date): boolean {
    return a.getUTCDate() !== b.getUTCDate()
      || a.getUTCMonth() !== b.getUTCMonth()
      || a.getUTCFullYear() !== b.getUTCFullYear();
  }

  private detectDurationDaysChanges(): void {
    this.form.controls.durationDays.valueChanges
      .subscribe((days: string) => {
        if (!days) {
          return;
        }

        const newValidTo = this.calculateExpirationDate(parseInt(days, 10));

        if (this.hasDateChanged(newValidTo, this.newExpirationDate)) {
          this.newExpirationDate = newValidTo;

          this.form.patchValue({
            validTo: this.formatDate.transform(this.newExpirationDate)
          });
        }
      });
  }

  private detectValidToChanges(): void {
    this.form.controls.validTo.valueChanges
      .subscribe((value: string | Date) => {
        // only accept datepicker changes
        if (!value || typeof value === 'string') {
          return;
        }

        const currentValidTo = this.calculateExpirationDate(this.form.controls.durationDays.value);

        let newValidTo = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0));
        newValidTo = this.parseValidTo(newValidTo.toISOString());

        if (this.hasDateChanged(currentValidTo, newValidTo)) {
          this.newExpirationDate = newValidTo;

          this.form.patchValue({
            durationDays: String(this.calculateDurationDays()),
            validTo: this.formatDate.transform(this.newExpirationDate)
          });
        }
      });
  }

  private detectFormChanges(): void {
    this.detectDurationDaysChanges();
    this.detectValidToChanges();
  }

  resetDuration(): void {
    this.form.patchValue({ durationDays: String(this.defaultDuration) });
  }

  ignoreDatepickerInput(evt: KeyboardEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
  }
}
